import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Text } from "../../../../components/common/typography";
import { Chip } from "../../../../components/common/chip";
import { SimpleTable, type SimpleTableColumn } from "../../../../components/common/simple-table";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { Pagination } from "../../../../components/common/pagination";
import { EmptyState } from "../../../../components/common/empty-state";
import { IconPackage } from "@tabler/icons-react";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { usePartnerProducts } from "../../hooks/usePartnerProducts";
import type { ProductDto } from "../../../../api";

const PAGE_SIZE = 10;

function fmtPrice(price?: number | null) {
  if (price == null) return "—";
  return price.toLocaleString("en-US", { maximumFractionDigits: 0 }) + " QAR";
}

interface PartnerProductsTabProps {
  partnerId: number;
}

export function PartnerProductsTab({ partnerId }: PartnerProductsTabProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading } = usePartnerProducts(partnerId, { page, pageSize: PAGE_SIZE });

  const columns = useMemo((): SimpleTableColumn<ProductDto>[] => [
    {
      key: "image",
      label: "",
      className: "w-12 shrink-0",
      render: (row) =>
        row.image ? (
          <img src={row.image} alt={row.name} className="h-10 w-10 rounded-lg object-cover border border-borderDefault" />
        ) : (
          <div className="h-10 w-10 rounded-lg bg-bgMuted border border-borderDefault flex items-center justify-center">
            <IconPackage size={16} className="text-textMuted" />
          </div>
        ),
    },
    {
      key: "name",
      label: "Product",
      className: "flex-[3] min-w-0",
      render: (row) => (
        <div className="flex flex-col gap-0.5 min-w-0">
          <Text variant="bodyBold" tone="dark" className="truncate">{row.name}</Text>
          {row.defaultCode && (
            <Text variant="bodyXs" tone="muted">SKU: {row.defaultCode}</Text>
          )}
        </div>
      ),
    },
    {
      key: "price",
      label: "Price",
      className: "flex-1",
      render: (row) => <Text variant="body" tone="dark">{fmtPrice(row.listPrice)}</Text>,
    },
    {
      key: "status",
      label: "Status",
      className: "flex-1",
      render: (row) => (
        <Chip variant={row.active ? "approved" : "rejected"}>
          {row.active ? "Active" : "Inactive"}
        </Chip>
      ),
    },
  ], []);

  const pageCount = data?.meta?.pageCount ?? 1;
  const products = data?.data ?? [];

  if (isLoading) {
    return (
      <ShimmerLoader message="Fetching products…">
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => <Shimmer key={i} className="h-14 w-full" />)}
        </div>
      </ShimmerLoader>
    );
  }

  if (products.length === 0) {
    return (
      <div className="rounded-xl border border-borderDefault bg-bgWhite">
        <EmptyState
          icon={IconPackage}
          title="No products"
          description="This seller has no active products listed."
        />
      </div>
    );
  }

  return (
    <SimpleTable
      columns={columns}
      data={products}
      keyExtractor={(p) => String(p.id)}
      onRowClick={(p) => navigate(AppRoutes.ADMIN_PRODUCT_DETAILS.replace(":id", String(p.id)))}
      footer={pageCount > 1 ? (
        <div className="flex justify-center">
          <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
        </div>
      ) : undefined}
    />
  );
}
